import analytics from '@react-native-firebase/analytics';
import {AppEventsLogger} from 'react-native-fbsdk-next';
import appsFlyer from 'react-native-appsflyer';
import {isAppsFlyerEnabled} from './appsflyer';

// Camada única de tracking: o evento é definido uma vez e cada sink traduz para o
// formato da sua plataforma. Canal novo = uma linha no array de sinks, nenhuma tela muda.
// No web, Firebase e Meta são aliasados para stubs (webpack.config.js).

export type EventParams = Record<string, any>;

export type Purchase = {
  value: number;
  currency: string;
  orderId?: string;
  coupon?: string;
};

type Sink = {
  name: string;
  logEvent: (event: string, params?: EventParams) => void;
  logPurchase: (purchase: Purchase) => void;
};

const firebase: Sink = {
  name: 'firebase',
  logEvent: (event, params) => analytics().logEvent(event, params),
  logPurchase: p =>
    analytics().logEvent('purchase', {
      // GA4 é case-sensitive: só `value`/`currency` minúsculos viram receita.
      value: p.value,
      currency: p.currency,
      transaction_id: p.orderId,
      coupon: p.coupon,
    }),
};

const meta: Sink = {
  name: 'meta',
  logEvent: (event, params) => AppEventsLogger.logEvent(event, params ?? {}),
  logPurchase: p =>
    // logPurchase (com valor), não logEvent: sem receita o Meta não faz otimização por valor.
    AppEventsLogger.logPurchase(
      p.value,
      p.currency,
      p.orderId ? {fb_content_id: p.orderId} : undefined,
    ),
};

const appsflyer: Sink = {
  name: 'appsflyer',
  logEvent: (event, params) => appsFlyer.logEvent(event, params ?? {}),
  logPurchase: p =>
    // af_purchase + af_revenue é o que o AppsFlyer repassa às redes para ROAS.
    appsFlyer.logEvent('af_purchase', {
      af_revenue: p.value,
      af_currency: p.currency,
      af_content_id: p.orderId,
      af_order_id: p.orderId,
    }),
};

const sinks: Sink[] = [firebase, meta, ...(isAppsFlyerEnabled ? [appsflyer] : [])];

// Um sink que falha não derruba os outros nem a tela que chamou.
function fanout(action: (sink: Sink) => void) {
  sinks.forEach(sink => {
    try {
      action(sink);
    } catch (error) {
      console.log(`📊 analytics sink ${sink.name} falhou:`, error);
    }
  });
}

export function trackEvent(event: string, params?: EventParams) {
  fanout(sink => sink.logEvent(event, params));
}

export function trackPurchase(purchase: Purchase) {
  fanout(sink => sink.logPurchase(purchase));
}
